import { Card, Chip, Grid, IconButton, Typography } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import FormSpacer from "@saleor/components/FormSpacer";
import {
  useDropOffLocationUpdate,
  useTaskAreaCreate,
  useTaskAreaUpdate
} from "@saleor/dropOff/mutation";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { GetTemplate_template_dropOffLocations_edges_node } from "@saleor/dropOff/types/GetTemplate";
import { TemplateAction } from "@saleor/dropOff/utils/handlers";
import useNotifier from "@saleor/hooks/useNotifier";
import { LogisticsStatusEnum } from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import React from "react";

import DropOffHomeLocationOutletStatus from "../DropOffHomeLocationOutletStatus";
import { DropOffHomeLocOutletIndicatorEnum } from "../DropOffHomeLocLabels";
import DropOffTextInput from "../DropOffTextInput";

interface DropOffHomeLocationListProps {
  mallId: string;
  templateId: string;
  homeLocations: GetTemplate_template_dropOffLocations_edges_node[];
  highlightHomeLoc: GetTemplate_template_dropOffLocations_edges_node;
  clearForm: () => void;
  refetch: () => any;
  editHomeLocationEvent: (
    homeLoc: GetTemplate_template_dropOffLocations_edges_node
  ) => void;
  templateAction: TemplateAction;
  userCanManageHomeLocations: boolean;
  userCanManageTaskAreas: boolean;
}

const DropOffHomeLocationList: React.FC<DropOffHomeLocationListProps> = props => {
  const {
    mallId,
    templateId,
    homeLocations,
    highlightHomeLoc,
    clearForm,
    refetch,
    editHomeLocationEvent,
    templateAction,
    userCanManageHomeLocations,
    userCanManageTaskAreas
  } = props;

  const classes = useTemplateStyles(props);
  const notify = useNotifier();

  const [taskAreaList, setTaskAreaList] = React.useState([]);

  const [updateHomeLocation] = useDropOffLocationUpdate({
    onCompleted: e => {
      if (e.dropOffLocationUpdate.dropOffLocationError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        clearForm();
        refetch();
      } else {
        notify({
          status: "error",
          text: e.dropOffLocationUpdate.dropOffLocationError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const clickDeleteHomeLocation = (
    homeLoc: GetTemplate_template_dropOffLocations_edges_node
  ) => {
    let txtMessage = "";

    if (homeLoc.defaultDropOffLocation === true && homeLocations.length === 1) {
      txtMessage = "Home location and task area cannot be empty";
    } else if (homeLoc.defaultDropOffLocation === true) {
      txtMessage = "Default home location cannot be deleted";
    }
    if (txtMessage.length > 0) {
      notify({
        status: "error",
        text: txtMessage
      });
      return;
    }

    updateHomeLocation({
      variables: {
        id: homeLoc.id,
        input: {
          code: homeLoc.code,
          contactNo: homeLoc.contactNo,
          defaultDropOffLocation: homeLoc.defaultDropOffLocation,
          dropOffAddress: homeLoc.dropOffAddress,
          latitude: homeLoc?.latitude ? homeLoc.latitude.toString() : "",
          longitude: homeLoc?.longitude ? homeLoc.longitude.toString() : "",
          mall: mallId,
          name: homeLoc.name,
          remarks: homeLoc.remarks,
          status: LogisticsStatusEnum.DELETED
        }
      }
    });
  };

  const clickEditHomeLocation = (
    homeLoc: GetTemplate_template_dropOffLocations_edges_node
  ) => {
    editHomeLocationEvent(homeLoc);
  };

  const [createTaskArea] = useTaskAreaCreate({
    onCompleted: e => {
      if (e.taskAreaCreate.taskAreaError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: e.taskAreaCreate.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [updateTaskArea] = useTaskAreaUpdate({
    onCompleted: e => {
      if (e.taskAreaUpdate.taskAreaError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: e.taskAreaUpdate.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const updateTaskAreaList = (index, newValue) => {
    setTaskAreaList(
      taskAreaList.map((str, i) => (i === index ? newValue : str))
    );
  };

  React.useEffect(() => {
    if (homeLocations && homeLocations.length > 0) {
      const taArr = [];
      homeLocations.forEach(() => {
        taArr.push("");
      });
      setTaskAreaList(taArr);
    }
  }, [homeLocations]);

  const homeLocationsSortedByOutletId = [
    ...homeLocations.filter(homeLoc => homeLoc.outletId),
    ...homeLocations.filter(homeLoc => !homeLoc.outletId)
  ];

  return (
    <Grid container spacing={2}>
      {homeLocationsSortedByOutletId?.map((homeLoc, index) => {
        if (homeLoc.status !== LogisticsStatusEnum.DELETED) {
          return (
            <Grid key={homeLoc?.id} item xs={6}>
              <Card
                className={`${
                  classes.dropOffHomeLocationCard
                } ${(highlightHomeLoc?.id !== undefined
                  ? highlightHomeLoc?.id === homeLoc.id
                  : homeLoc.defaultDropOffLocation === true) &&
                  classes.dropOffHomeLocationCardDefault}`}
                onClick={() => clickEditHomeLocation(homeLoc)}
              >
                <div className={classes.dropOffHomeLocationCardBody}>
                  <DropOffHomeLocationOutletStatus
                    visible={true}
                    status={
                      homeLoc.outletId
                        ? DropOffHomeLocOutletIndicatorEnum.REGISTERED
                        : DropOffHomeLocOutletIndicatorEnum.UNREGISTERED
                    }
                    registeredAt={homeLoc?.updatedAt}
                  />
                  <div className={classes.dropOffHomeLocationCardBodyContent}>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={10}
                        className={classes.displayFlexDispatchIcon}
                      >
                        <Typography
                          variant="h5"
                          className={classes.dropOffCardAccordionMediumTitle}
                        >
                          {homeLoc.name}
                          {homeLoc.defaultDropOffLocation === true && (
                            <Chip
                              label={"Default"}
                              size="small"
                              className={classes.dropOffChipDefault}
                            />
                          )}
                        </Typography>
                      </Grid>
                      {(templateAction === TemplateAction.EDIT ||
                        templateAction === TemplateAction.CREATE) && (
                        <Grid item xs={2} className={classes.circleContainer}>
                          <IconButton
                            id="deleteHomeLocBtn"
                            color="secondary"
                            onClick={() => clickDeleteHomeLocation(homeLoc)}
                            className={classes.padding0}
                            disabled={!userCanManageHomeLocations}
                          >
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                    <FormSpacer spacing={2} />

                    <Grid item xs={12}>
                      <DropOffTextInput
                        key={"homeLoc-taskAreaName-" + homeLoc?.id}
                        name={"homeLoc-taskAreaName-" + homeLoc?.id}
                        value={taskAreaList[index]}
                        disabled={
                          !homeLoc?.id ||
                          !userCanManageTaskAreas ||
                          templateAction === TemplateAction.VIEW
                        }
                        onCreate={() => {
                          createTaskArea({
                            variables: {
                              input: {
                                code: taskAreaList[index],
                                dropOffLocation: homeLoc?.id,
                                name: taskAreaList[index],
                                template: templateId
                              }
                            }
                          });
                        }}
                        change={c => {
                          if (c.target.value.length > 20) {
                            return;
                          }
                          updateTaskAreaList(index, c.target.value);
                        }}
                        buttonDisabled={!taskAreaList[index]}
                        placeHolder="Add Task Area"
                      />
                    </Grid>
                    <FormSpacer spacing={2} />

                    <Grid
                      container
                      spacing={2}
                      className={classes.dropOffHomeLocationCardBodyTaskAreas}
                    >
                      {homeLoc?.taskAreas &&
                        homeLoc?.taskAreas.map(ta => {
                          if (ta?.status !== LogisticsStatusEnum.DELETED) {
                            return (
                              <Grid key={ta?.id} item xs={6}>
                                <Chip
                                  key={"taskAreaChips-" + ta?.id}
                                  color="primary"
                                  label={ta?.name}
                                  onDelete={() => {
                                    updateTaskArea({
                                      variables: {
                                        id: ta?.id,
                                        input: {
                                          status: LogisticsStatusEnum.DELETED
                                        }
                                      }
                                    });
                                  }}
                                  deleteIcon={
                                    templateAction === TemplateAction.EDIT ||
                                    templateAction === TemplateAction.CREATE ? (
                                      <ClearIcon />
                                    ) : (
                                      <></>
                                    )
                                  }
                                  variant="default"
                                  className={classes.dropOffTaskAreaChip}
                                  disabled={!userCanManageTaskAreas}
                                />
                              </Grid>
                            );
                          }
                        })}
                    </Grid>
                  </div>
                </div>
              </Card>
            </Grid>
          );
        }
      })}
    </Grid>
  );
};

export default DropOffHomeLocationList;
